import React from "react";
import { Link } from "react-router-dom";

import { useGetCampaignObjectiveTypesQuery } from "hooks/api/dictionary/dictionary";
import { Button } from "ui/button";
import { Box } from "ui/box";
import { Stack } from "ui/stack";
import { Typography } from "ui/typography";
import Card, { CardBody, CardHeader, CardRow, type CardProps } from "components/partials/card/card";
import CampaignPageHeader from "containers/admin/clients/campaign/components/campaign-page-header/campaign-page-header";

import {
  useGetProgramTypesQuery,
  getDictionaryItemDescription,
  DEFAULT_PROGRAM_TYPE,
} from "hooks/api/dictionary";
import { CampaignAttributes, useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentUser } from "hooks/api/use-current-user";

import { hasPermission } from "utilities/user";

import { Permission } from "types/auth";

import { addHyphenToAudienceSelection, toSpacedTitleCase } from "utilities";

import Icons from "assets";

export interface ParamsType {
  campaignId?: string;
  clientId?: string;
  touchpointId?: string;
}

const CampaignSettingsPage = () => {
  const campaign = useCurrentCampaign();
  const currentUser = useCurrentUser();
  const canEdit = hasPermission(currentUser, Permission.PERM_CAMPAIGN_WRITE);

  return (
    <Box date-testid="client-campaign-settings-page">
      <div className="pt-3 pb-4">
        <CampaignPageHeader />
      </div>

      <Box sx={{ display: "grid" }}>
        <Stack direction="row" spacing="1.5rem" sx={{ alignItems: "flex-start" }}>
          <ClientCampaignBasicInfoCard campaign={campaign} canEdit={canEdit} />
          <ClientCampaignAudienceAndStrategyCard campaign={campaign} sx={{ mb: 3 }} />
        </Stack>
      </Box>
    </Box>
  );
};

interface ClientCampaignCardProps extends CardProps {
  campaign: CampaignAttributes;
  canEdit?: boolean;
}

const ClientCampaignBasicInfoCard: React.FC<ClientCampaignCardProps> = ({
  campaign,
  canEdit,
  ...rest
}) => {
  const { data: programTypes = [] } = useGetProgramTypesQuery();
  return (
    <Card {...rest}>
      <CardHeader heading="Basic details">
        {canEdit && (
          <Link to="edit">
            <Button
              data-testid="edit-campaign-button"
              variant="outlined"
              startIcon={<Icons.Edit />}>
              <span>Edit</span>
            </Button>
          </Link>
        )}
      </CardHeader>
      <CardBody>
        <CardRow title="Campaign Name" value={campaign.name} />
        <CardRow
          title="Program Type"
          value={getDictionaryItemDescription(programTypes, campaign.programType)}
        />
        {campaign.programType === DEFAULT_PROGRAM_TYPE && (
          <CardRow
            title="Transfer campaign"
            value={campaign.transferStudentStatusFlag ? "Enabled" : "Disabled"}
          />
        )}
        <CardRow title="Campaign CDN Profile" value={campaign.cdnSubdirectoryName} />
      </CardBody>
    </Card>
  );
};

const ClientCampaignAudienceAndStrategyCard: React.FC<ClientCampaignCardProps> = ({
  campaign,
  ...rest
}) => {
  const { data: campaignObjectiveTypes = [] } = useGetCampaignObjectiveTypesQuery(
    campaign.programType,
  );

  return (
    <Card {...rest}>
      <CardHeader heading={"Audience & Strategy"} />
      <CardBody>
        <CardRow
          title="Campaign Objective"
          value={
            campaign.campaignObjective && (
              // `CardRow` already wraps `value` in a span, and this text inherits the card's 14px.
              // `variant="inherit"` keeps that size and avoids nesting a `<p>` inside a `<span>`.
              <Typography variant="inherit" component="span" sx={{ textAlign: "right" }}>
                {campaign.campaignObjective &&
                  getDictionaryItemDescription(campaignObjectiveTypes, campaign.campaignObjective)}
              </Typography>
            )
          }
        />
        <CardRow
          title="Audience"
          value={
            campaign.audiences &&
            campaign.audiences.length > 0 && (
              <Box component="ul" sx={{ ml: "1.5rem", listStyleType: "none" }}>
                {campaign.audiences.map((audience) => {
                  return (
                    <Box
                      component="li"
                      key={audience}
                      sx={{ whiteSpace: "nowrap", textAlign: "right" }}
                    >
                      {addHyphenToAudienceSelection(toSpacedTitleCase(audience))}
                    </Box>
                  );
                })}
              </Box>
            )
          }
        />
      </CardBody>
    </Card>
  );
};

export default CampaignSettingsPage;
